// ============================================
// HOLISTIKA ERP/CRM TYPES (SSOT)
// Generated from Supabase schema
// ============================================

export type DatabaseTables = {
  companies: Company;
  contacts: Contact;
  project_intakes: ProjectIntake;
  projects: Project;
  interactions: Interaction;
  company_research: CompanyResearch;
}

// ============================================
// CORE ENTITIES
// ============================================

export interface Company {
  id: string;
  created_at: string;
  updated_at: string;
  
  // Basic info
  name: string;
  legal_name?: string;
  website_url?: string;
  industry?: string;
  company_size?: CompanySize;
  
  // Business intelligence
  current_maturity_level?: MaturityLevel;
  strategic_focus?: string[];
  known_pain_points?: string[];
  competitive_landscape?: string;
  business_model_notes?: string;
  
  // Financial context
  annual_revenue_estimate?: string;
  funding_stage?: FundingStage;
  budget_authority_level?: string;
  
  // Holistika relationship
  relationship_status: RelationshipStatus;
  primary_service_track?: HolistikaServiceTrack;
  total_lifetime_value: number;
  client_health_score: number;
  
  // Integration
  stripe_customer_id?: string;
  google_workspace_domain?: string;
  external_ids: Record<string, any>;
  
  // Metadata
  tags?: string[];
  custom_fields: Record<string, any>;
  research_notes?: string;
  created_by?: string;
  updated_by?: string;
}

export interface Contact {
  id: string;
  created_at: string;
  updated_at: string;
  company_id?: string;
  
  // Personal info
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  job_title?: string;
  department?: string;
  
  // Decision-making context
  decision_making_power?: DecisionMakingPower;
  seniority_level?: SeniorityLevel;
  
  // Communication
  preferred_contact_method: string;
  timezone?: string;
  availability_notes?: string;
  communication_style_notes?: string;
  
  // Relationship
  contact_source?: string;
  lead_score: number;
  engagement_level: EngagementLevel;
  last_interaction_at?: string;
  personal_interests?: string[];
  background_notes?: string;
  
  // Consent
  consent_marketing: boolean;
  consent_contact: boolean;
  gdpr_consent_date?: string;
  gdpr_consent_ip?: string;
  
  // Integration
  google_contact_id?: string;
  linkedin_profile_url?: string;
  
  created_by?: string;
  updated_by?: string;
}

export interface ProjectIntake {
  id: string;
  created_at: string;
  updated_at: string;
  company_id?: string;
  contact_id?: string;
  
  // Form data
  raw_form_data: Record<string, any>;
  service_track_requested: HolistikaServiceTrack;
  project_phase?: ProjectPhase;
  urgency: UrgencyLevel;
  budget_range?: BudgetRange;
  
  // Business intelligence
  current_situation: string;
  desired_outcome?: string;
  specific_challenges?: string[];
  success_metrics?: string;
  previous_consultancy_experience: boolean;
  stakeholders_involved?: string;
  decision_timeline?: string;
  
  // Qualification
  lead_score: number;
  qualification_status: QualificationStatus;
  assigned_to?: string;
  next_action?: string;
  follow_up_date?: string;
  
  // Research enrichment
  enrichment_status: EnrichmentStatus;
  company_research_data: Record<string, any>;
  competitive_analysis: Record<string, any>;
  market_research_data: Record<string, any>;
  
  // Attribution
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer_url?: string;
  landing_page?: string;
  ga4_client_id?: string;
  session_data: Record<string, any>;
  
  // Processing
  processed_at?: string;
  notification_sent_at?: string;
  research_completed_at?: string;
  n8n_workflow_triggered: boolean;
  google_calendar_event_id?: string;
  
  // Metadata
  form_version: string;
  ip_address?: string;
  user_agent?: string;
  custom_fields: Record<string, any>;
}

export interface Project {
  id: string;
  created_at: string;
  updated_at: string;
  intake_id?: string;
  company_id: string;
  primary_contact_id?: string;
  
  // Project definition
  name: string;
  description?: string;
  service_track: HolistikaServiceTrack;
  project_type?: HolistikaProjectType;
  
  // Phase tracking (Holistika methodology)
  current_phase: HolistikaPhase;
  phase_1_strategy_start_date?: string;
  phase_1_strategy_completed_at?: string;
  phase_2_tactics_start_date?: string;
  phase_2_tactics_completed_at?: string;
  phase_3_operations_start_date?: string;
  phase_3_operations_completed_at?: string;
  
  // Financial
  estimated_value?: number;
  actual_value?: number;
  stripe_subscription_id?: string;
  stripe_invoice_ids?: string[];
  billing_frequency?: BillingFrequency;
  payment_terms?: string;
  
  // Management
  status: ProjectStatus;
  priority: Priority;
  start_date?: string;
  target_completion_date?: string;
  actual_completion_date?: string;
  assigned_team_members: Record<string, any>;
  project_manager_id?: string;
  client_sponsor_contact_id?: string;
  
  // Deliverables
  deliverables_planned: Record<string, any>[];
  deliverables_completed: Record<string, any>[];
  client_success_metrics: Record<string, any>;
  client_satisfaction_score?: number;
  
  // Collaboration
  slack_channel_id?: string;
  shared_workspace_url?: string;
  project_documentation_url?: string;
  
  tags?: string[];
  custom_fields: Record<string, any>;
}

export interface Interaction {
  id: string;
  created_at: string;
  company_id?: string;
  contact_id?: string;
  project_id?: string;
  intake_id?: string;
  
  // Classification
  interaction_type: InteractionType;
  direction: 'inbound' | 'outbound';
  subject?: string;
  content?: string;
  outcome_summary?: string;
  sentiment?: 'positive' | 'neutral' | 'negative';
  
  // Scheduling
  scheduled_at?: string;
  duration_minutes?: number;
  timezone?: string;
  calendar_event_id?: string;
  meeting_link?: string;
  meeting_recording_url?: string;
  
  // Team context
  handled_by_email?: string;
  handled_by_name?: string;
  attendees: Record<string, any>;
  
  // Follow-up
  next_action?: string;
  follow_up_date?: string;
  action_owner?: string;
  
  // Automation
  triggered_by_automation: boolean;
  n8n_execution_id?: string;
  
  metadata: Record<string, any>;
}

export interface CompanyResearch {
  id: string;
  created_at: string;
  updated_at: string;
  company_id: string;
  
  // Research data
  linkedin_data: Record<string, any>;
  website_analysis: Record<string, any>;
  news_mentions: Record<string, any>;
  social_media_presence: Record<string, any>;
  financial_data: Record<string, any>;
  competitive_intel: Record<string, any>;
  
  // Technical analysis
  tech_stack_identified?: string[];
  digital_maturity_score?: number;
  ai_readiness_score?: number;
  
  // Market analysis
  market_position?: string;
  growth_stage?: string;
  funding_history: Record<string, any>;
  key_competitors?: string[];
  
  // Research metadata
  research_status: ResearchStatus;
  confidence_score: number;
  last_researched_at?: string;
  research_sources?: string[];
  enrichment_triggers: Record<string, any>;
  api_calls_made: Record<string, any>;
  
  custom_fields: Record<string, any>;
}

// ============================================
// ENUMS & CONSTANTS
// ============================================

export const COMPANY_SIZES = [
  'startup_0_10',
  'sme_10_100', 
  'midsize_100_1000',
  'enterprise_1000_plus'
] as const;

export type CompanySize = typeof COMPANY_SIZES[number];

export const HOLISTIKA_SERVICE_TRACKS = [
  'think_big_startup',
  'think_big_g2m', 
  'lift_ship_ops',
  'envoy_tech_lab',
  'mixed_engagement',
  'not_sure'
] as const;

export type HolistikaServiceTrack = typeof HOLISTIKA_SERVICE_TRACKS[number];

export const PROJECT_PHASES = [
  'strategy_phase1',
  'tactics_phase2', 
  'operations_phase3',
  'not_sure'
] as const;

export type ProjectPhase = typeof PROJECT_PHASES[number];

export const HOLISTIKA_PHASES = [
  'initiation',
  'planning',
  'execution', 
  'closure',
  'monitoring'
] as const;

export type HolistikaPhase = typeof HOLISTIKA_PHASES[number];

export const URGENCY_LEVELS = [
  'asap_2weeks',
  'this_quarter',
  'next_quarter', 
  'exploring'
] as const;

export type UrgencyLevel = typeof URGENCY_LEVELS[number];

export const BUDGET_RANGES = [
  'under_5k',
  '5k_15k',
  '15k_50k',
  '50k_plus',
  'not_disclosed'
] as const;

export type BudgetRange = typeof BUDGET_RANGES[number];

export const QUALIFICATION_STATUSES = [
  'new',
  'processing',
  'auto_qualified',
  'needs_manual_review',
  'qualified', 
  'demo_scheduled',
  'proposal_sent',
  'converted_to_project',
  'closed_won',
  'closed_lost',
  'nurture',
  'unqualified'
] as const;

export type QualificationStatus = typeof QUALIFICATION_STATUSES[number];

export const RELATIONSHIP_STATUSES = [
  'prospect',
  'qualified_lead',
  'active_client',
  'past_client',
  'partner'
] as const;

export type RelationshipStatus = typeof RELATIONSHIP_STATUSES[number];

export const DECISION_MAKING_POWERS = [
  'final_decision_maker',
  'budget_approver', 
  'influencer',
  'evaluator',
  'end_user',
  'unknown'
] as const;

export type DecisionMakingPower = typeof DECISION_MAKING_POWERS[number];

export const SENIORITY_LEVELS = [
  'c_suite',
  'vp_director',
  'manager',
  'senior_ic',
  'individual_contributor'
] as const;

export type SeniorityLevel = typeof SENIORITY_LEVELS[number];

export const ENGAGEMENT_LEVELS = [
  'cold',
  'warm', 
  'hot',
  'champion'
] as const;

export type EngagementLevel = typeof ENGAGEMENT_LEVELS[number];

export const INTERACTION_TYPES = [
  'form_submission',
  'email',
  'phone_call',
  'video_call',
  'in_person_meeting',
  'demo_presentation', 
  'proposal_sent',
  'contract_negotiation',
  'contract_signed',
  'payment_received',
  'project_kickoff',
  'deliverable_review',
  'project_closure',
  'support_request',
  'upsell_conversation',
  'referral_request'
] as const;

export type InteractionType = typeof INTERACTION_TYPES[number];

export const RESEARCH_STATUSES = [
  'pending',
  'in_progress', 
  'completed',
  'failed',
  'manual_review_needed'
] as const;

export type ResearchStatus = typeof RESEARCH_STATUSES[number];

export const ENRICHMENT_STATUSES = [
  'pending',
  'in_progress',
  'completed', 
  'failed'
] as const;

export type EnrichmentStatus = typeof ENRICHMENT_STATUSES[number];

export const MATURITY_LEVELS = [
  'ideation',
  'mvp',
  'market_entry',
  'scaling',
  'established',
  'transformation'
] as const;

export type MaturityLevel = typeof MATURITY_LEVELS[number];

export const FUNDING_STAGES = [
  'bootstrapped',
  'seed',
  'series_a',
  'series_b', 
  'growth',
  'public',
  'established'
] as const;

export type FundingStage = typeof FUNDING_STAGES[number];

export const PROJECT_TYPES = [
  'think_big_g2m',
  'think_big_boost',
  'lift_ship_ops',
  'envoy_tech_ai',
  'mixed_consulting',
  'custom_engagement'
] as const;

export type HolistikaProjectType = typeof PROJECT_TYPES[number];

export const PROJECT_STATUSES = [
  'proposal',
  'active',
  'on_hold',
  'completed',
  'cancelled'
] as const;

export type ProjectStatus = typeof PROJECT_STATUSES[number];

export const BILLING_FREQUENCIES = [
  'one_time',
  'monthly', 
  'quarterly',
  'milestone_based'
] as const;

export type BillingFrequency = typeof BILLING_FREQUENCIES[number];

export const PRIORITIES = [
  'low',
  'medium',
  'high',
  'urgent'
] as const;

export type Priority = typeof PRIORITIES[number];

export const INDUSTRIES = [
  'technology',
  'finance_banking',
  'healthcare',
  'manufacturing',
  'retail_ecommerce',
  'consulting_services',
  'energy_utilities',
  'other'
] as const;

export type Industry = typeof INDUSTRIES[number];

export const SPECIFIC_CHALLENGES = [
  'no_clear_strategy',
  'poor_project_execution', 
  'inefficient_processes',
  'digital_transformation_needs',
  'team_management_issues',
  'data_governance_problems',
  'market_positioning_unclear',
  'scaling_challenges',
  'ai_implementation_needed',
  'other'
] as const;

export type SpecificChallenge = typeof SPECIFIC_CHALLENGES[number];

export const DECISION_MAKER_LEVELS = [
  'yes_iam',
  'yes_with_approval',
  'no_but_influencer', 
  'no_just_research'
] as const;

export type DecisionMakerLevel = typeof DECISION_MAKER_LEVELS[number];

export const CONTACT_METHODS = [
  'email',
  'phone',
  'video_call'
] as const;

export type ContactMethod = typeof CONTACT_METHODS[number];

// ============================================
// API TYPES
// ============================================

export interface ProcessIntakeRequest {
  intake_data: ProjectIntakeFormData;
  session_metadata: SessionMetadata;
}

export interface ProcessIntakeResponse {
  success: boolean;
  intakeId: string;
  companyId: string;
  contactId: string;
  leadScore: number;
  assignedTo: string;
  qualificationStatus: QualificationStatus;
  researchTriggered: boolean;
  isNewCompany: boolean;
  isNewContact: boolean;
}

export interface ProjectIntakeFormData {
  // Contact details
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  jobTitle?: string;
  phone?: string;
  website?: string;
  
  // Company context
  companySize: CompanySize;
  industry: Industry;
  
  // Service context
  serviceTrack: HolistikaServiceTrack;
  projectPhase?: ProjectPhase;
  urgency: UrgencyLevel;
  budgetRange?: BudgetRange;
  
  // Business intelligence
  currentSituation: string;
  specificChallenges: SpecificChallenge[];
  desiredOutcome?: string;
  successMetrics?: string;
  
  // Decision context
  decisionMaker: DecisionMakerLevel;
  stakeholdersInvolved?: string;
  previousConsultancy: boolean;
  
  // Communication preferences
  preferredContactMethod: ContactMethod;
  bestTimeToContact?: string;
  
  // Consent
  consentContact: boolean;
  consentMarketing: boolean;
}

export interface SessionMetadata {
  pageUrl?: string;
  referrer?: string;
  userAgent?: string;
  ipAddress?: string;
  ga4ClientId?: string;
  utm?: {
    source?: string;
    medium?: string;
    campaign?: string;
    content?: string;
    term?: string;
  };
  timestamp?: string;
  [key: string]: any;
}

export interface IntakeDashboardData {
  summary: {
    total_intakes: number;
    high_value_leads: number;
    auto_qualified: number;
    avg_lead_score: number;
    conversion_rate: number;
  };
  by_service_track: Record<HolistikaServiceTrack, number>;
  by_team_assignment: Record<string, number>;
}

// ============================================ 
// N8N INTEGRATION TYPES
// ============================================

export interface N8nWebhookPayload {
  event: string;
  intakeId: string;
  companyId: string;
  contactId: string;
  companyName: string;
  contactName: string;
  email: string;
  leadScore: number;
  serviceTrack: HolistikaServiceTrack;
  urgency: UrgencyLevel;
  budgetRange?: BudgetRange;
  assignedTo: string;
  currentSituation: string;
  qualificationStatus: QualificationStatus;
  utm: {
    source?: string;
    medium?: string;
    campaign?: string;
  };
  sessionData: Record<string, any>;
}

export interface GoogleWorkspaceIntegration {
  calendarEventId?: string;
  meetingLink?: string;
  emailsSent?: string[];
  contactSynced?: boolean;
}

export interface StripeIntegration {
  customerId?: string;
  subscriptionId?: string;
  invoiceIds?: string[];
  paymentStatus?: string;
}

// ============================================
// UTILITY TYPES
// ============================================

export interface HolistikaTeamMember {
  email: string;
  name: string;
  role: string;
  specialization: HolistikaServiceTrack[];
  calendar_id?: string;
  slack_user_id?: string;
}

export interface AutomationConfig {
  n8n_webhook_url?: string;
  slack_webhook_url?: string;
  google_service_account_key?: string;
  research_apis_enabled: boolean;
  auto_qualification_threshold: number;
  auto_calendar_booking: boolean;
}

// Helper type for form validation
export type FormFieldKey = keyof ProjectIntakeFormData;

// Database query helpers
export type CompanyWithContacts = Company & {
  contacts: Contact[];
  recent_intakes?: ProjectIntake[];
  active_projects?: Project[];
};

export type ContactWithCompany = Contact & {
  company?: Company;
  recent_interactions?: Interaction[];
};

export type IntakeWithRelations = ProjectIntake & {
  company?: Company;
  contact?: Contact;
  interactions?: Interaction[];
  research?: CompanyResearch;
};

export type ProjectWithRelations = Project & {
  company?: Company;
  contact?: Contact;
  intake?: ProjectIntake;
  interactions?: Interaction[];
  team_members?: HolistikaTeamMember[];
};
